import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowTopRightOnSquareIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { mcpService, type ManagedMcpIntegration } from '../../services/mcpService';
import { Button } from '../ui/Button';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

interface WorkflowTriggerHubSpotPanelProps {
  entryAgentId?: string;
  workflowId?: string;
}

const isHubspotIntegration = (integration: ManagedMcpIntegration) => {
  const code = (integration.code || '').toLowerCase();
  const name = (integration.name || '').toLowerCase();
  return code.includes('hubspot') || name.includes('hubspot');
};

export const WorkflowTriggerHubSpotPanel: React.FC<WorkflowTriggerHubSpotPanelProps> = ({
  entryAgentId,
  workflowId,
}) => {
  const [integrations, setIntegrations] = useState<ManagedMcpIntegration[]>([]);
  const [loading, setLoading] = useState(false);

  const loadIntegrations = useCallback(async () => {
    if (!entryAgentId) {
      setIntegrations([]);
      return;
    }
    setLoading(true);
    try {
      const result = await mcpService.listManagedIntegrations(entryAgentId);
      setIntegrations(result.items || []);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load entry agent integrations');
    } finally {
      setLoading(false);
    }
  }, [entryAgentId]);

  useEffect(() => {
    void loadIntegrations();
  }, [loadIntegrations]);

  const hubspotIntegration = useMemo(
    () => integrations.find(isHubspotIntegration),
    [integrations]
  );

  const isConnected = Boolean(hubspotIntegration?.connection?.isEnabled);
  const profileId = hubspotIntegration?.connection?.profileId;
  const hubspotWebhookUrl = profileId
    ? `${API_BASE}/core/webhooks/hubspot/crm?profileId=${encodeURIComponent(profileId)}`
    : `${API_BASE}/core/webhooks/hubspot/crm?profileId={connection-profile-id}`;

  const copyText = (text: string, label: string) => {
    void navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  if (!entryAgentId) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        Select an outbound voice entry agent to configure HubSpot lead sync.
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-blue-100 bg-blue-50/50 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">HubSpot Outbound</p>
          <p className="mt-1 text-xs text-gray-500">
            Outbound calls start from HubSpot contacts synced to leads. Connect HubSpot on the entry agent, publish this
            workflow, and pending contacts are dialed automatically every 2 minutes.
          </p>
        </div>
        <Link
          to={`/agents/${entryAgentId}/config?tab=integrations`}
          className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-blue-700 hover:bg-gray-50"
        >
          Integrations
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs">
        <span className="font-medium text-gray-700">HubSpot status: </span>
        {loading ? (
          <span className="text-gray-500">Checking…</span>
        ) : hubspotIntegration ? (
          <span className={isConnected ? 'text-green-700' : 'text-amber-700'}>
            {isConnected ? 'Connected' : 'Not connected — open Integrations to authorize'}
          </span>
        ) : (
          <span className="text-amber-700">HubSpot integration not enabled for this agent type</span>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-600">HubSpot CRM webhook</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 break-all rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-[11px] text-blue-700">
            POST {hubspotWebhookUrl}
          </code>
          <Button tone="custom"
            type="button"
            onClick={() => copyText(hubspotWebhookUrl, 'HubSpot webhook URL')}
            className="flex-shrink-0 rounded border border-gray-200 p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
          </Button>
        </div>
        <ol className="list-decimal space-y-1 pl-4 text-xs text-gray-500">
          <li>Open the entry agent Integrations tab and connect HubSpot (OAuth + webhook signing secret).</li>
          <li>In HubSpot, subscribe to contact creation and property changes pointing at the webhook URL above.</li>
          <li>Publish this workflow with trigger type <strong>outbound-call</strong>.</li>
          <li>New HubSpot contacts become callable leads; the cron dials them via Twilio.</li>
        </ol>
      </div>

      {workflowId ? (
        <p className="text-xs text-gray-400">
          Workflow must be <strong>published</strong> for HubSpot leads to route to this workflow (
          <code className="rounded bg-white px-1">{workflowId}</code>).
        </p>
      ) : null}
    </div>
  );
};
